import { useState, useEffect, useCallback, useRef } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useWorkspaceRoles } from "@/hooks/useWorkspaceRoles";
import { WORKSPACE_LOGO_ACCEPT, WORKSPACE_LOGO_MAX_BYTES } from "@/api/workspacesApi";
import WorkspaceIcon from "@/components/feature/WorkspaceIcon";
import DeleteWorkspaceModal from "@/components/feature/DeleteWorkspaceModal";
import { Banner, SectionHeading } from "./settingsUi";

function WorkspaceCard() {
  const {
    activeWorkspace,
    activeWorkspaceId,
    workspaces,
    renameWorkspace,
    uploadLogo,
    removeLogo,
    deleteWorkspace,
    loading,
  } = useWorkspace();
  const { isAdmin, loading: rolesLoading } = useWorkspaceRoles();
  const canDeleteWorkspace = isAdmin && !rolesLoading && workspaces.length > 1;
  const [wsName, setWsName] = useState("");
  const [wsBu, setWsBu] = useState("");
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const fileRef = useRef(null);

  const logoUrl = activeWorkspace?.logoUrl ?? null;

  useEffect(() => {
    setWsName(activeWorkspace?.name ?? "");
    setWsBu(activeWorkspace?.businessUnit ?? "");
    setBanner(null);
    setDeleteOpen(false);
  }, [activeWorkspace?.id, activeWorkspace?.name, activeWorkspace?.businessUnit]);

  const onPickLogo = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = ""; // allow re-selecting the same file
      if (!file || !activeWorkspaceId) return;
      if (!WORKSPACE_LOGO_ACCEPT.split(",").includes(file.type)) {
        setBanner({ type: "err", text: "Please choose a PNG or JPG image." });
        return;
      }
      if (file.size > WORKSPACE_LOGO_MAX_BYTES) {
        setBanner({ type: "err", text: "Image must be 2 MB or smaller." });
        return;
      }
      setLogoBusy(true);
      setBanner(null);
      try {
        await uploadLogo({ workspaceId: activeWorkspaceId, file });
        setBanner({ type: "ok", text: "Workspace icon updated." });
      } catch (err) {
        setBanner({ type: "err", text: err instanceof Error ? err.message : "Could not upload icon." });
      } finally {
        setLogoBusy(false);
      }
    },
    [activeWorkspaceId, uploadLogo]
  );

  const onRemoveLogo = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLogoBusy(true);
    setBanner(null);
    try {
      await removeLogo({ workspaceId: activeWorkspaceId });
      setBanner({ type: "ok", text: "Workspace icon removed." });
    } catch (err) {
      setBanner({ type: "err", text: err instanceof Error ? err.message : "Could not remove icon." });
    } finally {
      setLogoBusy(false);
    }
  }, [activeWorkspaceId, removeLogo]);

  const save = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setSaving(true);
    setBanner(null);
    try {
      await renameWorkspace({ workspaceId: activeWorkspaceId, name: wsName, businessUnit: wsBu });
      setBanner({ type: "ok", text: "Workspace updated." });
    } catch (e) {
      setBanner({ type: "err", text: e instanceof Error ? e.message : "Could not save." });
    } finally {
      setSaving(false);
    }
  }, [activeWorkspaceId, renameWorkspace, wsName, wsBu]);

  const onConfirmDelete = useCallback(async () => {
    if (!activeWorkspaceId) throw new Error("No workspace selected.");
    await deleteWorkspace({ workspaceId: activeWorkspaceId });
  }, [activeWorkspaceId, deleteWorkspace]);

  if (loading && !activeWorkspaceId) {
    return <p className="text-sm text-slate-500">Loading workspace…</p>;
  }
  if (!activeWorkspaceId) {
    return <p className="text-sm text-slate-500">Pick a workspace from the sidebar to edit its profile.</p>;
  }

  return (
    <div className="space-y-5">
      <SectionHeading
        label="Workspace"
        desc="The name and business unit label shown across the dashboard and exports."
      />

      {banner && <Banner type={banner.type} text={banner.text} onDismiss={() => setBanner(null)} />}

      <div>
        <span className="mb-1.5 block text-sm font-semibold text-slate-800">Workspace icon</span>
        <div className="flex items-center gap-4">
          <WorkspaceIcon name={wsName} logoUrl={logoUrl} size="xl" />
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="ui-btn-secondary"
                disabled={logoBusy || saving}
                onClick={() => fileRef.current?.click()}
              >
                {logoBusy ? "Working…" : logoUrl ? "Change icon" : "Upload icon"}
              </button>
              {logoUrl && (
                <button
                  type="button"
                  className="ui-btn-secondary"
                  disabled={logoBusy || saving}
                  onClick={() => void onRemoveLogo()}
                >
                  Remove
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500">PNG or JPG, up to 2 MB. Square images look best.</p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept={WORKSPACE_LOGO_ACCEPT}
            className="hidden"
            onChange={onPickLogo}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="ws-name" className="mb-1.5 block text-sm font-semibold text-slate-800">
            Workspace name
          </label>
          <input id="ws-name" className="ui-field" value={wsName} onChange={(e) => setWsName(e.target.value)} disabled={saving} />
        </div>
        <div>
          <label htmlFor="ws-bu" className="mb-1.5 block text-sm font-semibold text-slate-800">
            Business unit label
          </label>
          <input
            id="ws-bu"
            className="ui-field"
            value={wsBu}
            onChange={(e) => setWsBu(e.target.value)}
            placeholder="e.g. B2C · Retail"
            disabled={saving}
          />
        </div>
      </div>

      <button
        type="button"
        className="ui-btn-primary"
        disabled={saving || !wsName.trim()}
        onClick={() => void save()}
      >
        {saving ? "Saving…" : "Update workspace"}
      </button>

      {isAdmin && !rolesLoading ? (
        <div className="border-t border-slate-200 pt-6">
          <SectionHeading
            label="Danger zone"
            desc="Permanently delete this workspace. Members lose access and the Keycloak client is removed."
          />
          {workspaces.length < 2 ? (
            <p className="mt-3 text-sm text-slate-500">
              You cannot delete the last remaining workspace. Create another workspace first.
            </p>
          ) : (
            <button
              type="button"
              className="mt-3 rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 disabled:opacity-60"
              disabled={saving || logoBusy || !canDeleteWorkspace}
              onClick={() => setDeleteOpen(true)}
            >
              Delete workspace
            </button>
          )}
        </div>
      ) : null}

      <DeleteWorkspaceModal
        open={deleteOpen}
        workspaceName={activeWorkspace?.name ?? wsName}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}

export default function GeneralSettingsSection() {
  return (
    <div className="mx-auto max-w-6xl space-y-2">
      <div className="mb-6">
        <h3 className="text-xl font-semibold tracking-tight text-slate-900">Workspace</h3>
        <p className="mt-1 text-sm text-slate-500">Workspace name and business unit for the active workspace.</p>
      </div>

      <div className="ui-card-static !p-6 sm:!p-8">
        <WorkspaceCard />
      </div>
    </div>
  );
}
